/**
 * 「VoIPオプション」に関連するロジック用ファイルです。
 */
import { Rvoip, Voip } from '@/types/api/mypage/options';
import { VoipOption, VoipOptionPriceResponse } from '@/types/api/mypage/voipoptionprice';
import {
  containCatchPhone,
  containNumberDisplay,
  containNumberRequest,
  containStopNuisanceCall,
  containVoiceWarp,
} from '@/utils/common';
import { Constants } from '@/utils/constants';
import { VoipOptionUseStatus } from '@/utils/constants/voip';

/**
 * 「単品オプション」が「サービスパック」に含まれているかどうかを取得する。
 * 単品の申込/解約可否を判断する前提として、パック内包かどうかを切り分ける。
 * @param classType 単品オプション種別
 * @param servicePackType サービスパック種別
 * @returns 含まれている場合は、trueを返却する。それ以外は、falseを返却する。
 */
export const isIncludeVoipOptionServicePack = (
  classType: string,
  servicePackType: string
): boolean => {
  if (classType === Constants.VoipOptionClassType.VOIP_NUMBER_DISPLAY) {
    return containNumberDisplay(servicePackType);
  }
  if (classType === Constants.VoipOptionClassType.VOIP_NUMBER_INFO) {
    return containNumberRequest(servicePackType);
  }
  if (classType === Constants.VoipOptionClassType.VOIP_CATCH) {
    return containCatchPhone(servicePackType);
  }
  if (classType === Constants.VoipOptionClassType.VOIP_TRANSFER) {
    return containVoiceWarp(servicePackType);
  }
  if (classType === Constants.VoipOptionClassType.VOIP_BLOCK_CALL) {
    return containStopNuisanceCall(servicePackType);
  }
  return false;
};

/**
 * 「サービスパック」の「利用中」の情報を取得する。
 * 現在有効なサービスパックは 1 件だけを前提に、変更画面の基準値として使う。
 * @param voipOptionPrice VoIPオプション利用状況取得のレスポンス
 * @returns サービスパック情報
 */
export const getServicePackServiceStart = (
  voipOptionPrice: VoipOptionPriceResponse
): VoipOption | undefined => {
  if (!voipOptionPrice?.service_packs) {
    return undefined;
  }
  return voipOptionPrice.service_packs.find((e) => {
    return e.use_status === VoipOptionUseStatus.SERVICE;
  });
};

/**
 * 「サービスパック」の「利用中」のクラス区分コードを取得する。
 * 画面側がレスポンス構造をたどらずに、現在選択中パック種別だけ参照できるようにする。
 * @param voipOptionPrice VoIPオプション利用状況取得のレスポンス
 * @returns クラス種別コード
 */
export const getServicePackServiceStartClassTypeCode = (
  voipOptionPrice: VoipOptionPriceResponse
): string => {
  const option = getServicePackServiceStart(voipOptionPrice);
  if (!option) {
    // 未契約時は専用の「なし」定数を返し、undefined 分岐を呼び出し元へ持ち込まない。
    return Constants.VoipOptionServicePack.SERVICE_PACK_NONE;
  }
  // @ts-ignore
  return Constants.VoipOptionServicePackClassType[option.option_class_type];
};

/**
 * 「単品オプション」の状態を取得する。
 * API レスポンス上の enum 名と画面側で使う classType を対応付けて検索する。
 * @param classType 単品オプション種別
 * @param voipOptionPrice VoIPオプション利用状況取得のレスポンス
 * @returns 該当のVoIPオプション情報を返却する。
 */
export const getVoipOption = (
  classType: string,
  voipOptionPrice: VoipOptionPriceResponse
): VoipOption | undefined => {
  if (!voipOptionPrice?.voip_options) {
    return undefined;
  }
  const voipOptionClassType = Object.keys(Constants.VoipOptionClassType).find(
    // @ts-ignore
    (e) => Constants.VoipOptionClassType[e] === classType
  );
  return voipOptionPrice.voip_options.find((e) => {
    return e.option_class_type === voipOptionClassType;
  });
};

/**
 * 「サービスパックオプション」の状態を取得する。
 * 単品オプションとは別配列で管理されるため、専用取得関数を分けて呼び出し側を単純化する。
 * @param servicePackType サービスパックオプション種別
 * @param voipOptionPrice VoIPオプション利用状況取得のレスポンス
 * @returns 該当のVoIPオプション情報を返却する。
 */
export const getVoipOptionServicePack = (
  servicePackType: string,
  voipOptionPrice: VoipOptionPriceResponse
): VoipOption | undefined => {
  if (!voipOptionPrice?.voip_options) {
    return undefined;
  }
  const type = Object.keys(Constants.VoipOptionServicePackClassType).find(
    // @ts-ignore
    (e) => Constants.VoipOptionServicePackClassType[e] === servicePackType
  );
  return voipOptionPrice.service_packs.find((e) => {
    return e.option_class_type === type;
  });
};

/**
 * VoIPオプションが契約中かどうかの情報を返却する
 * VoIP と RVoIP で状態定義が異なるため、共通の「利用中」判定へ寄せる。
 * @param voip VoIPオプション情報
 * @param rvoip RVoIPオプション情報
 * @returns いずれかのVoIPオプションが契約中の場合は、trueを返却する。それ以外は、falseを返却する
 */
export const isVoipOptionStatusUsing = ({
  voip,
  rvoip,
}: {
  voip?: Voip;
  rvoip?: Rvoip;
}): boolean => {
  if (voip) {
    // 「解約中」の状態は、サービスは利用可能なため利用中と判断する
    const isVoipUse = [
      Constants.VoipOptionStatus.SERVICE,
      Constants.VoipOptionStatus.QUIT_WITHOUT,
      Constants.VoipOptionStatus.QUIT_REQUESTED,
    ].includes(voip.use_status);
    if (isVoipUse) {
      return true;
    }
  }
  if (rvoip) {
    // 「解約中」の状態は、サービスは利用可能なため利用中と判断する
    const isRVoipUse = [
      Constants.RvoipOptionStatus.SERVICE,
      Constants.RvoipOptionStatus.QUIT_QUITTIG,
      Constants.RvoipOptionStatus.QUIT_REQUESTED,
    ].includes(rvoip.rvoip_use_state);
    if (isRVoipUse) {
      return true;
    }
  }
  // どちらの系統にも有効契約がない場合だけ false とする。
  return false;
};
